import type { TypologyServiceTakeoffInput } from '@obraplan/core/application/contracts/schedule-from-takeoff';
import type { ProjectStructureView } from '@obraplan/core/application/use-cases/structure/get-project-structure';
import type { ProjectGeometry } from '@obraplan/core/application/use-cases/takeoff/geometry-use-cases';
import type { ProjectServiceItems } from '@obraplan/core/application/use-cases/takeoff/service-items-use-cases';
import type { ProjectTakeoff } from '@obraplan/core/application/use-cases/takeoff/takeoff-use-cases';
import { serviceLaborHours } from '@obraplan/core/domain/schedule/schedule-from-takeoff';
import type { Service } from '@obraplan/core/domain/takeoff/service';
import { emptyCatalog, type ServiceCatalog } from '@obraplan/core/domain/takeoff/service-catalog';
import {
  catalogoDoServidor,
  geometriaParaRascunho,
  itensDoServidor,
  lancamentosDoServidor,
  montarWorkspace,
  pavimentosDaObra,
} from '@/features/takeoff/do-servidor';
import { montarEscopoDaObra, recortarTipologia } from '@/features/takeoff/escopo-da-tipologia';
import {
  medidosDaTipologia,
  quantidadesDosServicos,
  totaisDaTipologia,
} from '@/features/takeoff/medido-da-tipologia';
import {
  comodosDaTipologia,
  fundacaoDaTipologia,
  pileDaTipologia,
  quantitiesOf,
} from '@/features/takeoff/resumo-dos-itens';
import {
  emptyWorkspace,
  workspaceReducer,
  type TypologyDraft,
  type WorkspaceDraft,
} from '@/features/takeoff/typology-draft';

/**
 * O quantitativo que o cronograma recebe (Fase 31, RN-415).
 *
 * O número de cada serviço sai do desenho e das decisões da tipologia, e essa conta mora no
 * navegador (`src/features/takeoff`) — o servidor não a refaz. A tela manda, então, o
 * quantitativo **por tipologia × serviço**, com o serviço pela chave natural (disciplina,
 * serviço), e o servidor soma por pavimento pelas posições do desenho.
 *
 * Duas partes, as duas puras:
 *
 * - `bancadaDaObra` monta a bancada da obra só com o que o servidor guarda — a mesma
 *   costura que a tela do quantitativo faz, sem o rascunho do navegador e sem gravar nada;
 * - `linhasDoQuantitativo` lê cada tipologia como a aba Orçamento lê: o recorte do catálogo
 *   da tipologia, a quantidade na unidade do serviço e o HH dos itens de mão de obra em hora.
 */

/** As cinco leituras da obra de que a bancada precisa — as mesmas da tela do quantitativo. */
export interface LeiturasDoQuantitativo {
  readonly estrutura: ProjectStructureView;
  readonly quantitativo: ProjectTakeoff;
  readonly geometria: ProjectGeometry;
  readonly servicos: readonly Service[];
  readonly itens: ProjectServiceItems;
}

/**
 * A bancada da obra montada do servidor, sem o navegador.
 *
 * O caminho é o do `useWorkspaceDraft`, passo a passo: as tipologias da espinha, o desenho,
 * o cadastro de serviços com os itens e o que cada tipologia lançou, e a identidade da obra
 * por cima (`montarWorkspace`). Fica de fora só o que ainda mora no `localStorage` — a
 * divergência de um apto —, que o cronograma não usa: o servidor soma por tipologia.
 */
export function bancadaDaObra(leituras: LeiturasDoQuantitativo): WorkspaceDraft {
  const { estrutura, quantitativo, geometria, servicos, itens } = leituras;

  const pavimentos = pavimentosDaObra(quantitativo);
  const comTipologias = workspaceReducer(emptyWorkspace(), {
    type: 'SYNC_TYPOLOGIES',
    unitTypes: quantitativo.unitTypes.map((unitType) => ({
      unitTypeId: unitType.id,
      name: unitType.name,
      kind: unitType.kind,
      floorKind: pavimentos.get(unitType.id) ?? 'TIPO',
    })),
  });
  const comDesenho = geometriaParaRascunho(comTipologias, geometria);

  const nomes = new Map(
    estrutura.availableDisciplines.map((disciplina) => [String(disciplina.id), disciplina.name]),
  );
  const comServicos =
    servicos.length === 0
      ? emptyCatalog()
      : catalogoDoServidor(emptyCatalog(), servicos, (id) => nomes.get(String(id)));
  const lancamentos = lancamentosDoServidor(itens);
  const comItens = workspaceReducer(comDesenho, {
    type: 'HYDRATE_ITEMS',
    catalog: itensDoServidor(comServicos, itens, servicos),
    typologies: quantitativo.unitTypes.map((unitType) => {
      const decidido = lancamentos[String(unitType.id)];
      return {
        unitTypeId: unitType.id,
        name: unitType.name,
        kind: unitType.kind,
        itemQuantities: decidido?.itemQuantities ?? {},
        itemReasons: decidido?.itemReasons ?? {},
        serviceQuantities: decidido?.serviceQuantities ?? {},
        ...(decidido?.sheetName === undefined ? {} : { sheetName: decidido.sheetName }),
        ...(decidido?.pieceQuantities === undefined
          ? {}
          : { pieceQuantities: decidido.pieceQuantities }),
      };
    }),
  });

  return montarWorkspace(comItens, estrutura, quantitativo);
}

/** Número que o contrato aceita: finito e não negativo; o resto é "sem número". */
function numeroOuNulo(valor: number | undefined): number | null {
  return valor === undefined || !Number.isFinite(valor) || valor < 0 ? null : valor;
}

/**
 * O quantitativo de cada tipologia × serviço, na forma que o cronograma recebe.
 *
 * Para cada tipologia da obra (`daObra`), o recorte do catálogo dela (`recorteDe`, sem o
 * filtro do topo) e, em cada serviço:
 *
 * - a **quantidade** na unidade do serviço — a `inServiceUnit` do quantitativo, com as peças
 *   importadas (RN-232, RN-384);
 * - o **HH** — a soma dos itens de mão de obra medidos em hora (`serviceLaborHours`), cada
 *   um com a quantidade que o quantitativo resolve para ele.
 *
 * Só vai a linha com alguma coisa a fazer (quantidade ou HH maior que zero): é a mesma regra
 * do servidor (RN-415), e a linha zerada só engordaria a chamada. A tipologia que não é da
 * obra — a em branco que segura a bancada vazia — fica de fora.
 */
export function linhasDoQuantitativo(
  workspace: WorkspaceDraft,
  recorteDe: (typology: TypologyDraft) => ServiceCatalog,
  daObra: ReadonlySet<string>,
): TypologyServiceTakeoffInput[] {
  const linhas: TypologyServiceTakeoffInput[] = [];
  for (const typology of workspace.typologies) {
    if (!daObra.has(String(typology.unitTypeId))) continue;
    const catalog = recorteDe(typology);
    if (catalog.disciplines.length === 0) continue;

    const totals = totaisDaTipologia(workspace, typology);
    // A fundação com fator e taxa por peça (Fase 41): o medido do aço e a parte de cada peça.
    const fundacao = fundacaoDaTipologia(workspace, typology);
    const quantidades = quantidadesDosServicos(catalog, totals, typology, fundacao);
    const quantities = quantitiesOf(
      typology,
      medidosDaTipologia(catalog, totals, typology, fundacao),
      catalog,
      pileDaTipologia(typology, workspace.pileTypes),
      comodosDaTipologia(workspace, typology),
      fundacao,
    );

    for (const disciplina of catalog.disciplines) {
      for (const service of disciplina.services) {
        const quantity = numeroOuNulo(quantidades.get(service.key)?.inServiceUnit);
        const laborHours = numeroOuNulo(serviceLaborHours(service, quantities));
        if ((quantity ?? 0) <= 0 && (laborHours ?? 0) <= 0) continue;
        linhas.push({
          unitTypeId: String(typology.unitTypeId),
          disciplineName: disciplina.name,
          serviceName: service.name,
          quantity,
          laborHours,
        });
      }
    }
  }
  return linhas;
}

/**
 * O quantitativo da obra inteira a partir das cinco leituras: a bancada, o recorte de cada
 * tipologia (o que ela escolheu, nas disciplinas dos andares dela) e as linhas.
 */
export function quantitativoDaObra(
  leituras: LeiturasDoQuantitativo,
): TypologyServiceTakeoffInput[] {
  const workspace = bancadaDaObra(leituras);
  const escopo = montarEscopoDaObra(leituras.estrutura, leituras.quantitativo, leituras.servicos);
  const catalogo = workspace.catalog ?? emptyCatalog();
  return linhasDoQuantitativo(
    workspace,
    (typology) => recortarTipologia(catalogo, typology, escopo).catalogo,
    new Set(leituras.quantitativo.unitTypes.map((unitType) => String(unitType.id))),
  );
}
